import "@/components/Layout/BaseLayouts/StickyEditCard.scss";

import clsx from "clsx";
import { ReactNode } from "react";

import { CardContainer } from "@/components/Layout/BaseLayouts/CardContainer";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { useFixStickyCardScrollAfterKeyboardClose } from "@/hooks/useFixStickyCardScrollAfterKeyboardClose";
import { useIsMobile } from "@/hooks/useIsMobile";
import { FCC } from "@/types";

export interface ProfileCardProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /**
   * Body copy that belongs to the title (e.g. what a setting toggle in
   * `actions` does). Sits in the header 8px below the title instead of in the
   * body, whose own padding would put 24px between the two.
   */
  description?: ReactNode;
  actions?: ReactNode;
  /**
   * When `true`, pins the card to the viewport on mobile (portal header and
   * bottom nav hidden) so title and actions remain visible while the body
   * scrolls inside. Defaults to `true` on `"ivory"`, `false` on `"white"`;
   * pass explicitly to override either default.
   */
  stickyHeader?: boolean;
  /**
   * `"profile"` caps the card width (default on `"ivory"`).
   * `"none"` lets the surrounding grid control width (default on `"white"`).
   * Pass explicitly to override either default.
   */
  maxWidth?: "profile" | "none";
  stackActionsOnMobile?: boolean;
  /**
   * Card surface variant. Defaults to `"ivory"`. Switching to `"white"` also
   * flips the `stickyHeader` and `maxWidth` defaults, since every `"white"`
   * call site uses the non-sticky, uncapped layout.
   */
  variant?: "ivory" | "white";
  /**
   * Gap between the body's direct children. `4` (16px, default) suits a
   * stack of form fields, e.g. LoginData's email/password cards. `6` (24px)
   * and `2` (8px) suit dire-container-style info-tile summaries instead
   * (Figma node 2608:15287, "Zusammenfassung") — group tiles that belong
   * together in their own `gap-2` wrapper first, since this only sets one
   * uniform gap across all direct children.
   */
  bodyGap?: 2 | 4 | 6;
  className?: string;
  dataTestid?: string;
}

const bodyGapClass: Record<NonNullable<ProfileCardProps["bodyGap"]>, string> = {
  2: "gap-2",
  4: "gap-4",
  6: "gap-6",
};

export const ProfileCard: FCC<ProfileCardProps> = ({
  title,
  subtitle,
  description,
  actions,
  variant = "ivory",
  stickyHeader = variant !== "white",
  maxWidth = variant === "white" ? "none" : "profile",
  stackActionsOnMobile,
  bodyGap = 4,
  children,
  className,
  dataTestid,
}) => {
  const isMobile = useIsMobile();
  useBodyScrollLock(stickyHeader && isMobile);
  const stickyBodyRef = useFixStickyCardScrollAfterKeyboardClose(stickyHeader);

  return (
    <CardContainer
      dataTestid={dataTestid}
      className={clsx(
        // No overflow clipping here — ListBox/Combobox flyouts must be able to
        // paint outside the card. Sticky mobile mode sets overflow via
        // .sticky-edit-card SCSS instead.
        "flex w-full flex-col rounded-lg",
        maxWidth === "profile" && "md:max-w-[428px]",
        stickyHeader && "sticky-edit-card",
        className,
      )}
      variant={variant}
    >
      <div
        className={clsx(
          "sticky-edit-card__header flex shrink-0 items-start gap-3 px-4 pb-2 pt-4",
          stackActionsOnMobile && "flex-col tablet:flex-row",
        )}
      >
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-2 py-2">
          <div className="flex flex-col gap-1">
            <div className="truncate-safe w-full text-body-base font-semibold leading-none text-foreground-primary">
              {title}
            </div>
            {subtitle && (
              <p className="text-body-sm leading-tight text-foreground-secondary">
                {subtitle}
              </p>
            )}
          </div>
          {description && (
            <div className="text-body-sm leading-none tracking-[0.28px] text-foreground-secondary">
              {description}
            </div>
          )}
        </div>
        {actions && (
          <div className="flex shrink-0 items-center gap-3">{actions}</div>
        )}
      </div>
      {/* Skipped entirely when there is no content: the design's header-only
          card is exactly header plus the 8px footer, and an empty body would
          still add its vertical padding. */}
      {children && (
        <div
          className="sticky-edit-card__body flex items-start px-4 py-2"
          ref={stickyBodyRef}
        >
          <div
            className={clsx(
              "flex w-full flex-1 flex-col",
              bodyGapClass[bodyGap],
            )}
          >
            {children}
          </div>
        </div>
      )}
      <div className="h-2 w-full shrink-0" />
    </CardContainer>
  );
};
